import { useState, useEffect, useRef } from 'react';

const WARNING_DURATION = 10;  // 10 seconds before logout

function SessionTimeout({
    timeout,
    onTimeout,
}: {
    timeout: number;
    onTimeout: () => void;
}) {
    const [showModal, setShowModal] = useState(false);
    const [remainingTime, setRemainingTime] = useState(WARNING_DURATION);
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null); // Use ref to persist countdownInterval
    
    const resetTimeout = () => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current); // browser window function
        }
        timeoutIdRef.current = setTimeout(() => {
            setShowModal(true);
            startCountdown();
        }, timeout);
    };

    const startCountdown = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
        countdownIntervalRef.current = setInterval(() => {
        setRemainingTime((prevTime) => {
            if (prevTime <= 1) {
                if (countdownIntervalRef.current !== null) {
                    clearInterval(countdownIntervalRef.current);
                    countdownIntervalRef.current = null;
                }

                setShowModal(false);

                setTimeout(() => {
                    onTimeout();
                }, 0);

                return 0;
                }

                return prevTime - 1;
            });
        }, 1000);   // the function will be called every 1000 milliseconds, or once per second
    };

    const handleContinue = () => {
        setShowModal(false);
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
        setRemainingTime(WARNING_DURATION); // Reset remainingTime here
        resetTimeout();
    };

    useEffect(() => {
        const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];

        // Debounced resetTimeout to avoid excessive calls
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const debouncedReset = () => {            
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            timeoutId = setTimeout(() => {
                resetTimeout();
            }, 100); // Adjust debounce delay as needed
        };

        events.forEach((event) => {
            window.addEventListener(event, debouncedReset);
        });
        resetTimeout();

        return () => {
            events.forEach((event) => {
                window.removeEventListener(event, debouncedReset);
            });
            clearTimeout(timeoutId);
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, [timeout, onTimeout]); // Add dependencies to ensure updates propagate correctly

    return (
        <>
            {showModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: 'var(--app-surface-bg, #fff)',
                            color: 'var(--app-text-color, #343133)',
                            padding: '30px',
                            borderRadius: '8px',
                            textAlign: 'center',
                        }}
                    >
                        <p className="labelMediumContent">Your session will expire in {remainingTime} Seconds.</p>
                        <button className="primary-button" onClick={handleContinue}>Continue</button>
                    </div>
                </div>
            )}
        </>
    );
}

export default SessionTimeout;

